import { Download, ExternalLink, File as FileIcon, FileSpreadsheet, FileText, Presentation } from "lucide-react";
import { ArtifactCardShell } from "@/components/chat/ArtifactCardShell";
import { isPreviewableFile, openDocumentPreview } from "@/components/chat/DocumentPreviewModal";
import type { StreamingArtifact } from "@/hooks/useChatStreaming";
import { downloadAttachment } from "@/lib/downloadAttachment";

interface ArtifactCardProps {
  artifact: StreamingArtifact;
}

const EXTENSION_ICONS: Record<string, typeof FileText> = {
  xlsx: FileSpreadsheet,
  xls: FileSpreadsheet,
  csv: FileSpreadsheet,
  pptx: Presentation,
  ppt: Presentation,
};

const EXTENSION_LABELS: Record<string, string> = {
  docx: "Word Document",
  doc: "Word Document",
  xlsx: "Excel Spreadsheet",
  xls: "Excel Spreadsheet",
  csv: "CSV File",
  pptx: "PowerPoint Presentation",
  ppt: "PowerPoint Presentation",
  pdf: "PDF Document",
};

function getExtension(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() ?? "";
}

/**
 * Inline card rendered in the chat message list for any AI-generated file
 * that isn't HTML (docx, pptx, xlsx, pdf, csv, etc. — anything produced by
 * `dynamic_session`/`container_skill`). Mirrors HtmlArtifactCard's look, but
 * previewable types (pdf/docx/xlsx/pptx) open the DocumentPreviewPanel and
 * everything else downloads directly on click.
 */
export function ArtifactCard({ artifact }: Readonly<ArtifactCardProps>) {
  const ext = getExtension(artifact.filename);
  const Icon = EXTENSION_ICONS[ext] || FileIcon;
  const label = EXTENSION_LABELS[ext] || "Generated File";
  const canPreview = isPreviewableFile(artifact.filename);
  const versionLabel = artifact.version && artifact.version > 1 ? `v${artifact.version}` : null;

  async function downloadFile() {
    try {
      await downloadAttachment({ attachmentUuid: artifact.attachment_uuid, url: artifact.url }, artifact.filename);
    } catch (err) {
      console.error("Failed to download artifact:", err);
    }
  }

  function handleClick() {
    if (canPreview && artifact.attachment_uuid) {
      openDocumentPreview(artifact.attachment_uuid, artifact.filename);
    } else {
      downloadFile();
    }
  }

  return (
    <ArtifactCardShell
      icon={Icon}
      title={artifact.filename}
      subtitle={`${label} — ${canPreview ? "Click to preview" : "Click to download"}`}
      versionLabel={versionLabel}
      onClick={handleClick}
      actions={
        <>
          {canPreview && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                downloadFile();
              }}
              className="p-1.5 rounded hover:bg-interactive-hover text-content-tertiary hover:text-content-primary transition"
              title="Download"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          )}
          {canPreview ? (
            <ExternalLink className="w-4 h-4 text-content-quaternary" />
          ) : (
            <Download className="w-4 h-4 text-content-quaternary" />
          )}
        </>
      }
    />
  );
}
